// src/pages/AdminPanel.js
import { useEffect, useState } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy, where, getDoc,
} from 'firebase/firestore/lite';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, Link } from 'react-router-dom';
import { db, functions } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_CATEGORIES } from '../utils/surveyQuestions';
import { seedEchoEcho } from '../scripts/seedEchoEcho';
import styles from './AdminPanel.module.css';
import PageLoading from '../components/PageLoading';

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const ROLE_BADGE = {
  free:        'gray',
  team_member: 'green',
  team_leader: 'blue',
  consultant:  'purple',
  admin:       'red',
};

// ─── Tab: My Teams ────────────────────────────────────────────────────────────

function MyTeamsTab({ myEmail }) {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [asConsultant, asLeader] = await Promise.all([
          getDocs(query(collection(db, 'teams'), where('consultantEmails', 'array-contains', myEmail))),
          getDocs(query(collection(db, 'teams'), where('leaderEmail', '==', myEmail))),
        ]);
        const seen = new Set();
        const all = [];
        [...asConsultant.docs, ...asLeader.docs].forEach(d => {
          if (!seen.has(d.id)) { seen.add(d.id); all.push({ id: d.id, ...d.data() }); }
        });
        setTeams(all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [myEmail]);

  if (loading) return <PageLoading text="Loading your teams..." />;

  if (teams.length === 0) {
    return (
      <div className={`card ${styles.emptyCard}`}>
        <div className={styles.emptyIcon}>👥</div>
        <h3 className={styles.emptyTitle}>No teams yet</h3>
        <p className={styles.emptyText}>Teams where you're listed as leader or consultant will appear here.</p>
      </div>
    );
  }

  return (
    <div className={styles.teamList}>
      {teams.map(team => (
        <button
          key={team.id}
          onClick={() => navigate(`/meetings/${team.id}`)}
          className={styles.teamCard}
        >
          <div className={styles.teamCardBody}>
            <div className={styles.teamCardTop}>
              <div className="avatar">{team.name?.charAt(0) || '?'}</div>
              <div>
                <div className={styles.teamName}>{team.name}</div>
                <div className={styles.teamMeta}>
                  {team.memberEmails?.length || 0} members{team.leaderEmail && ` · Led by ${team.leaderEmail}`}
                </div>
              </div>
            </div>
            <div className={styles.teamCardBadges}>
              <span className="badge badge-gray">{team.memberEmails?.length || 0} members</span>
            </div>
          </div>
          <span className={styles.chevronLg}>›</span>
        </button>
      ))}
    </div>
  );
}

// ─── Tab: All Teams ───────────────────────────────────────────────────────────

function AllTeamsTab({ teams, users, onTeamUpdated }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [expanded, setExpanded] = useState(null);
  const [teamStats, setTeamStats] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [leaderEmail, setLeaderEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);

  const userByEmail = {};
  users.forEach(u => { if (u.email) userByEmail[u.email] = u; });

  async function loadTeamStats(teamId) {
    if (teamStats[teamId]) return;
    try {
      const [meetingsSnap, responsesSnap] = await Promise.all([
        getDocs(query(collection(db, 'meetings'), where('teamId', '==', teamId))),
        getDocs(query(collection(db, 'responses'), where('teamId', '==', teamId))),
      ]);
      const meetingDates = meetingsSnap.docs
        .map(d => d.data().date?.toDate ? d.data().date.toDate() : (d.data().date ? new Date(d.data().date) : null))
        .filter(Boolean)
        .sort((a, b) => b - a);
      setTeamStats(prev => ({
        ...prev,
        [teamId]: {
          meetingCount: meetingsSnap.size,
          lastMeeting: meetingDates[0] ? meetingDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—',
          assessmentCount: responsesSnap.size,
        },
      }));
    } catch (err) {
      console.error('loadTeamStats failed:', err);
    }
  }

  async function createTeam(e) {
    e.preventDefault();
    if (!teamName || !leaderEmail) return;
    setCreating(true);
    setCreateMsg(null);
    const normalizedLeaderEmail = leaderEmail.trim().toLowerCase();
    try {
      const teamRef = await addDoc(collection(db, 'teams'), {
        name: teamName, leaderEmail: normalizedLeaderEmail, memberEmails: [normalizedLeaderEmail], consultantEmails: [],
        status: 'pending', customQuestions: DEFAULT_CATEGORIES, createdAt: serverTimestamp(),
      });
      const inviteRef = await addDoc(collection(db, 'invitations'), {
        email: normalizedLeaderEmail, role: 'team_leader', teamId: teamRef.id, teamName, status: 'pending', createdAt: serverTimestamp(),
      });
      try {
        const sendInvite = httpsCallable(functions, 'sendInviteEmail');
        await sendInvite({ to: normalizedLeaderEmail, role: 'team_leader', teamName, inviteToken: inviteRef.id, inviterName: userProfile?.displayName || userProfile?.email });
      } catch {}
      setCreateMsg({ type: 'success', text: `Team "${teamName}" created.` });
      setTeamName(''); setLeaderEmail('');
      onTeamUpdated();
    } catch (err) {
      console.error(err);
      setCreateMsg({ type: 'error', text: 'Failed to create team.' });
    } finally {
      setCreating(false);
    }
  }

  async function deleteTeam(teamId, teamName) {
    const confirmed = window.confirm(
      `Delete team "${teamName}"?\n\nThis removes the team record only. Members and their assessment data are not deleted.`
    );
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'teams', teamId));
      onTeamUpdated();
    } catch (err) {
      console.error('Delete team failed:', err);
      alert('Failed to delete team. Please try again.');
    }
  }

  return (
    <div>
      <div className={styles.actionRow}>
        <button className={showCreate ? 'btn btn-secondary' : 'btn btn-primary'} onClick={() => { setShowCreate(s => !s); setCreateMsg(null); }}>
          {showCreate ? '✕ Cancel' : '+ Create New Team'}
        </button>
      </div>

      {showCreate && (
        <div className={`card ${styles.createCard}`}>
          <div className="card-title">Create New Team</div>
          <div className="card-subtitle">Creates a separate team group — each team has its own members, assessments, and meetings.</div>
          {createMsg && (
            <div className={`alert alert-${createMsg.type === 'error' ? 'error' : 'success'} ${styles.alertMb}`}>{createMsg.text}</div>
          )}
          <form onSubmit={createTeam}>
            <div className="grid-2">
              <div className="form-group">
                <label>Team name</label>
                <input type="text" value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="e.g. Product Team" required />
              </div>
              <div className="form-group">
                <label>Team leader email</label>
                <input type="email" value={leaderEmail} onChange={e => setLeaderEmail(e.target.value)} placeholder="leader@company.com" required />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? <><span className="spinner" /> Creating...</> : 'Create & send invite →'}
            </button>
          </form>
        </div>
      )}

      {teams.length === 0 ? (
        <div className={`card ${styles.emptyCard}`}>
          <p className={styles.emptyText}>No teams yet. Create one above.</p>
        </div>
      ) : (
        <div className={styles.teamsList}>
          {teams.map(t => {
            const isExpanded = expanded === t.id;
            const memberCount = t.memberEmails?.length || 0;
            const consultantCount = t.consultantEmails?.length || 0;

            return (
              <div key={t.id} className={`card ${styles.cardFlush}`}>
                <button
                  onClick={() => { const next = isExpanded ? null : t.id; setExpanded(next); if (next) loadTeamStats(next); }}
                  className={styles.teamRowBtn}
                >
                  <div className={styles.avatarSm}>{t.name?.charAt(0) || '?'}</div>
                  <div className={styles.flexMin}>
                    <div className={styles.rowName}>{t.name}</div>
                    <div className={styles.rowMeta}>
                      {t.leaderEmail} · {memberCount} member{memberCount !== 1 ? 's' : ''}
                      {consultantCount > 0 && ` · ${consultantCount} consultant${consultantCount !== 1 ? 's' : ''}`}
                      · Created {fmtDate(t.createdAt)}
                    </div>
                  </div>
                  <span className={styles.rowChevron} style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }}>›</span>
                </button>

                {isExpanded && (
                  <div className={styles.expandedBody}>
                    {/* #13 fix: className="admin-stat-row" — collapses from 4-col to 2×2 on mobile */}
                    <div className="admin-stat-row">
                      {[
                        { label: 'Meetings',             value: teamStats[t.id] != null ? teamStats[t.id].meetingCount : '…' },
                        { label: 'Members',              value: memberCount },
                        { label: 'Assessments', value: teamStats[t.id] != null ? teamStats[t.id].assessmentCount : '…' },
                        { label: 'Last Meeting',         value: teamStats[t.id] != null ? teamStats[t.id].lastMeeting : '…' },
                      ].map(s => (
                        <div key={s.label} className={`stat-card ${styles.statCardSm}`}>
                          <div className="stat-label">{s.label}</div>
                          <div className={`stat-value ${styles.statValueLg}`}>{s.value}</div>
                        </div>
                      ))}
                    </div>

                    {memberCount > 0 && (
                      <div>
                        <div className={styles.sectionLabel}>Team Members</div>
                        <div className={styles.pillsRow}>
                          {t.memberEmails.map(email => {
                            const u = userByEmail[email];
                            return (
                              <span key={email} className={styles.memberPill}>
                                {u?.displayName || email}
                                {!u && <span className={styles.pendingIcon}>⏳</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {consultantCount > 0 && (
                      <div>
                        <div className={styles.sectionLabel}>Consultants</div>
                        <div className={styles.pillsRow}>
                          {t.consultantEmails.map(email => (
                            <span key={email} className={styles.consultantPill}>
                              {userByEmail[email]?.displayName || email}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className={styles.teamActions}>
                      <button className={`btn btn-primary ${styles.btnSm}`} onClick={() => navigate(`/meetings/${t.id}`)}>Open Team Hub →</button>
                      <button className={`btn btn-secondary ${styles.btnSm}`} onClick={() => navigate(`/meetings/new?teamId=${t.id}`)}>＋ New Meeting</button>
                      <button className={`btn btn-danger ${styles.btnSm}`} onClick={() => deleteTeam(t.id, t.name)}>🗑 Delete Team</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Users ── #12 fix: wrap table in overflow-x: auto div ────────────────

function UsersTab({ users, onRoleChange, onDisableUser, onUserAdded, currentUser, userProfile }) {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [confirmDisable, setConfirmDisable] = useState(null);
  const [disabling, setDisabling] = useState(null);
  const [disableError, setDisableError] = useState(null);

  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('team_member');
  const [adding, setAdding] = useState(false);
  const [addMessage, setAddMessage] = useState(null);

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      (u.displayName || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || u.role === filterRole;
    return matchSearch && matchRole;
  });

  async function handleAddUser() {
    if (!newEmail) return;
    setAdding(true);
    setAddMessage(null);
    const normalizedEmail = newEmail.trim().toLowerCase();
    try {
      const inviteRef = await addDoc(collection(db, 'invitations'), {
        email: normalizedEmail,
        role: newRole,
        teamId: null,
        teamName: 'the platform',
        invitedBy: currentUser.uid,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      try {
        const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
        await sendInviteEmail({ to: normalizedEmail, role: newRole, teamName: 'the platform', inviteToken: inviteRef.id, inviterName: userProfile?.displayName });
      } catch (e) {
        console.warn('Email send failed:', e.message);
      }
      setAddMessage({ type: 'success', text: `Invitation sent to ${normalizedEmail}` });
      setNewName('');
      setNewEmail('');
      setNewRole('team_member');
      onUserAdded();
    } catch (err) {
      console.error(err);
      setAddMessage({ type: 'error', text: 'Failed to send invitation.' });
    } finally {
      setAdding(false);
    }
  }

  async function handleDisable(userId, userEmail) {
    setDisabling(userId);
    setDisableError(null);
    try {
      await onDisableUser(userId, userEmail);
      setConfirmDisable(null);
    } catch (err) {
      setDisableError('Failed to remove user. Please try again.');
    } finally {
      setDisabling(null);
    }
  }

  function handleRoleChange(userId, newRoleValue) {
    if (newRoleValue === 'remove') {
      const u = users.find(u => u.id === userId);
      setConfirmDisable({ id: userId, email: u?.email });
    } else {
      onRoleChange(userId, newRoleValue);
    }
  }

  return (
    <div>
      {/* Add New User */}
      <div className={`card ${styles.cardMb}`}>
        <div className="card-title">Add New User</div>
        <div className="card-subtitle">Send an invitation email. The user will register via the link.</div>

        {addMessage && (
          <div className={`alert alert-${addMessage.type === 'error' ? 'error' : 'success'} ${styles.alertInline}`}>
            {addMessage.text}
          </div>
        )}

        <div className={styles.formRow}>
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Display name (optional)" className={styles.formInput} style={{ flex: '1 1 160px' }} />
          <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@company.com" className={styles.formInput} style={{ flex: '2 1 200px' }} />
          <select value={newRole} onChange={e => setNewRole(e.target.value)} className={styles.formSelect} style={{ flex: '1 1 140px' }}>
            {['free', 'team_member', 'team_leader', 'consultant', 'admin'].map(r => (
              <option key={r} value={r}>{r.replace('_', ' ')}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={handleAddUser} disabled={adding || !newEmail}>
            {adding ? <span className="spinner" /> : 'Send invite →'}
          </button>
        </div>
      </div>

      {/* Confirm disable dialog */}
      {confirmDisable && (
        <div className={styles.confirmBox}>
          <div className={styles.confirmTitle}>Remove {confirmDisable.email}?</div>
          <div className={styles.confirmText}>
            This will disable their login, remove them from all teams, and revoke app permissions. Their account will remain but they cannot log in.
          </div>
          {disableError && <div className={styles.errorText}>{disableError}</div>}
          <div className={styles.btnRow}>
            <button className="btn btn-danger" onClick={() => handleDisable(confirmDisable.id, confirmDisable.email)} disabled={!!disabling}>
              {disabling ? '…' : 'Yes, remove access'}
            </button>
            <button className="btn btn-secondary" onClick={() => { setConfirmDisable(null); setDisableError(null); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className={styles.filterRow}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…" className={styles.searchInput} />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className={styles.filterSelect}>
          <option value="all">All roles</option>
          {['free', 'team_member', 'team_leader', 'consultant', 'admin'].map(r => (
            <option key={r} value={r}>{r.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      {/* overflow-x wrapper for mobile */}
      <div className={styles.tableWrap}>
        <div className={`card ${styles.tableCard}`}>
          <div className={styles.tableHeader}>
            <span>Name</span><span>Email</span><span>Role</span><span>Change Role</span><span>Joined</span><span>Impersonate</span>
          </div>

          {filtered.length === 0 ? (
            <div className={styles.tableEmpty}>No users match your filters.</div>
          ) : (
            filtered.map((u, i) => (
              <div
                key={u.id}
                className={styles.tableRow}
                style={{
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                  background: i % 2 === 0 ? 'transparent' : 'var(--bg-soft)',
                  opacity: u.disabled ? 0.5 : 1,
                }}
              >
                <div className={styles.cellName}>
                  {u.displayName || <span className={styles.noName}>No name</span>}
                  {u.disabled && <span className={styles.removedBadge}>REMOVED</span>}
                </div>
                <div className={styles.cellEmail}>{u.email}</div>
                <div><span className={`badge badge-${ROLE_BADGE[u.role] || 'gray'}`}>{(u.role || 'free').replace('_', ' ')}</span></div>
                <div>
                  <select value={u.role || 'free'} onChange={e => handleRoleChange(u.id, e.target.value)} className={styles.roleSelect} disabled={u.disabled}>
                    <option value="remove" className={styles.dangerOption}>— Remove user —</option>
                    {['free', 'team_member', 'team_leader', 'consultant', 'admin'].map(r => (
                      <option key={r} value={r}>{r.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.cellDate}>{fmtDate(u.createdAt)}</div>
                <div>
                  {(() => {
                    const eligible = (u.role === 'team_member' || u.role === 'team_leader') && !u.disabled;
                    return (
                      <button
                        className={styles.impersonateBtn}
                        disabled={!eligible}
                        title={u.disabled ? 'Account disabled' : !eligible ? 'Only available for team members/leaders' : 'View the app as this user (read-only)'}
                        onClick={() => {
                          startImpersonation({ uid: u.id, ...u });
                          navigate('/dashboard');
                        }}
                      >
                        View as →
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.tableFooter}>
        Showing {filtered.length} of {users.length} users
      </div>
    </div>
  );
}

// ─── Tab: Seed Data ───────────────────────────────────────────────────────────

function SeedTab({ currentUser, onDone }) {
  const [status, setStatus] = useState(null);

  async function handleSeed() {
    setStatus('running');
    try {
      await seedEchoEcho(currentUser.uid);
      setStatus('done');
      onDone();
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  }

  return (
    <div className={`card ${styles.seedCard}`}>
      <div className="card-title">🌱 Seed Demo Data</div>
      <div className={`card-subtitle ${styles.subtitleMb}`}>
        Populates Firestore with the <strong>"Echo Echo Echo"</strong> demo team — 7 users, 5 meetings, 6 assessment responses, 5 consultant observations.
      </div>
      <div className={styles.detailList}>
        {[
          { label: 'Team', value: '"Echo Echo Echo" with status active' },
          { label: 'Users', value: '7 (leader + 6 members with Working Genius profiles)' },
          { label: 'Meetings', value: '5 completed meetings with entries + reports' },
          { label: 'Responses', value: '6 assessment responses across all categories' },
          { label: 'Observations', value: '5 consultant observation sets' },
        ].map(row => (
          <div key={row.label} className={styles.detailRow}>
            <span className={styles.detailLabel}>{row.label}</span>
            <span className={styles.detailValue}>{row.value}</span>
          </div>
        ))}
      </div>
      {status === 'done' && <div className={`alert alert-success ${styles.alertMbLg}`}>✓ Seed complete! The "Echo Echo Echo" team is now in Firestore.</div>}
      {status === 'error' && <div className={`alert alert-error ${styles.alertMbLg}`}>Seed failed — check the browser console for details.</div>}
      <button className={`btn btn-secondary ${styles.btnMinW}`} onClick={handleSeed} disabled={status === 'running'}>
        {status === 'running' ? <><span className="spinner" /> Running seed…</> : status === 'done' ? '✓ Run Again' : 'Run Seed →'}
      </button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'all-teams', label: '🏢 All Teams' },
  { id: 'my-teams',  label: '👤 My Teams'  },
  { id: 'users',     label: '👥 Users'     },
  { id: 'seed',      label: '🌱 Seed Data' },
];

export default function AdminPanel() {
  const { currentUser, userProfile } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all-teams');

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [teamsSnap, usersSnap] = await Promise.all([
        getDocs(query(collection(db, 'teams'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc'))),
      ]);
      setTeams(teamsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function changeUserRole(userId, newRole) {
    await updateDoc(doc(db, 'users', userId), { role: newRole });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
  }

  async function disableUser(userId, userEmail) {
    userEmail = (userEmail || '').toLowerCase();
    try {
      const disableUserFn = httpsCallable(functions, 'disableUser');
      await disableUserFn({ uid: userId });

      const teamsWithUser = await getDocs(query(
        collection(db, 'teams'),
        where('memberEmails', 'array-contains', userEmail)
      ));
      const teamsAsConsultant = await getDocs(query(
        collection(db, 'teams'),
        where('consultantEmails', 'array-contains', userEmail)
      ));
      const updates = [];
      teamsWithUser.forEach(t => updates.push(
        updateDoc(doc(db, 'teams', t.id), { memberEmails: (t.data().memberEmails || []).filter(e => e !== userEmail) })
      ));
      teamsAsConsultant.forEach(t => updates.push(
        updateDoc(doc(db, 'teams', t.id), { consultantEmails: (t.data().consultantEmails || []).filter(e => e !== userEmail) })
      ));
      await Promise.all(updates);

      await updateDoc(doc(db, 'users', userId), { role: 'free' });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: 'free', disabled: true } : u));
    } catch (err) {
      console.error('Failed to disable user:', err);
      throw err;
    }
  }

  if (loading) return <PageLoading text="Loading admin panel…" />;

  const myEmail = (currentUser?.email || userProfile?.email || '').toLowerCase();

  return (
    <div className="fade-up">
      <div className="page-header">
        <h2>Admin Panel</h2>
        <p>
          {teams.length} team{teams.length !== 1 ? 's' : ''} ·{' '}
          {users.length} user{users.length !== 1 ? 's' : ''} ·{' '}
          {users.filter(u => u.role === 'team_leader').length} leaders ·{' '}
          {users.filter(u => u.role === 'consultant').length} consultants
        </p>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={styles.tabBtn}
            style={{
              borderBottom: `2.5px solid ${tab === t.id ? 'var(--olive)' : 'transparent'}`,
              color: tab === t.id ? 'var(--olive)' : 'var(--ink-muted)',
              fontWeight: tab === t.id ? 700 : 500,
            }}
          >
            {t.label}
            {t.id === 'all-teams' && (
              <span className={styles.tabCount} style={{ background: tab === t.id ? 'var(--olive-glow)' : 'var(--bg-soft)', color: tab === t.id ? 'var(--olive)' : 'var(--ink-muted)' }}>{teams.length}</span>
            )}
            {t.id === 'users' && (
              <span className={styles.tabCount} style={{ background: tab === t.id ? 'var(--olive-glow)' : 'var(--bg-soft)', color: tab === t.id ? 'var(--olive)' : 'var(--ink-muted)' }}>{users.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'my-teams'  && <MyTeamsTab myEmail={myEmail} />}
      {tab === 'all-teams' && <AllTeamsTab teams={teams} users={users} onTeamUpdated={loadData} />}
      {tab === 'users'     && <UsersTab users={users} onRoleChange={changeUserRole} onDisableUser={disableUser} onUserAdded={loadData} currentUser={currentUser} userProfile={userProfile} />}
      {tab === 'seed'      && <SeedTab currentUser={currentUser} onDone={loadData} />}
    </div>
  );
}
